import React, { useEffect, useState, useContext, useCallback } from 'react';
import { fetchData, getPreferences } from '../../services/apiService';
import './issues.css';
import Create from '../create/Create';
import Update from '../update/Update';
import ContextMenu from '../contextMenu/ContextMenu';
import DeleteConfirmationModal from '../delete/Delete'; // Import the delete confirmation modal
import PreferencesModal from '../preferences/Preferences';
import { deleteIssue } from '../../services/apiService'; // Import your deleteIssue function
import Logo from '../../assets/images/SPM.png';
import { UserContext } from '../userContext/UserContext';

const Issues = () => {
    const [issues, setIssues] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [showResolved, setShowResolved] = useState(false);
    const [showClosed, setShowClosed] = useState(false);
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [selectedIssues, setSelectedIssues] = useState([]);
    // Keep track of selected preferences (job numbers) as before:
    const [selectedPreferences, setSelectedPreferences] = useState([]);
    const [preferences, setPreferences] = useState([]);

    // New state for filtering by Internal/External
    const [internalFilter, setInternalFilter] = useState(true);
    const [externalFilter, setExternalFilter] = useState(false);

    const today = new Date();

    const { userId } = useContext(UserContext);

    // Handle selecting/deselecting an issue
    const handleIssueSelection = (issue, event) => {
        event.stopPropagation(); // Prevent row click when checkbox is clicked
        setSelectedIssues((prevSelected) => {
            if (prevSelected.includes(issue)) {
                return prevSelected.filter((selectedIssue) => selectedIssue !== issue);
            } else {
                return [...prevSelected, issue];
            }
        });
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        return date.toISOString().slice(0, 10);
    };

    const loadData = useCallback(async () => {
        try {
            const listData = await fetchData();
            const formattedData = listData.map(issue => ({
                ...issue,
                DueDate: formatDate(issue.DueDate),
                DateReported: formatDate(issue.DateReported),
            }));
            setIssues(formattedData);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, []);

    const loadPreferences = useCallback(async () => {
        try {
            const prefData = await getPreferences(userId);
            setPreferences(prefData);
        } catch (error) {
            console.error("Couldn't fetch preferences");
        }
    }, [userId]);

    useEffect(() => {
        loadData();
        loadPreferences();
    }, [loadData, loadPreferences]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleRowClick = (issue) => {
        setSelectedIssue(issue);
        setShowUpdateModal(true);
    };

    const handleRowRightClick = (issue, event, index) => {
        event.preventDefault();
        setSelectedIssue(issue);
        setContextMenuPosition({ x: event.pageX, y: event.pageY });
        setSelectedRowIndex(index);
        setShowContextMenu(true);
    };

    const handleClickOutside = () => {
        setShowContextMenu(false);
        setSelectedRowIndex(null);
    };

    const handleUpdateClick = () => {
        setShowContextMenu(false);
        setShowUpdateModal(true);
    };

    const handleDeleteClick = () => {
        setShowContextMenu(false);
        setShowDeleteModal(true);
    };

    // Toggle preference (job number) selection as before
    const handlePreferenceClick = (id) => {
        setSelectedPreferences(prevSelected => 
            prevSelected.includes(id)
                ? prevSelected.filter(item => item !== id)
                : [...prevSelected, id]
        );
    };

    const confirmDelete = async () => {
        try {
            await deleteIssue(selectedIssue);
            setShowDeleteModal(false);
            setIssues(prevIssues => prevIssues.filter(issue => issue.id !== selectedIssue.id));
            setSelectedIssue(null);
        } catch (error) {
            console.error('Error deleting issue:', error);
        }
    };

    const getDueDateClass = (dueDate) => {
        if (!dueDate) return '';
        const date = new Date(dueDate);
        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'due-today';
        } else if (diffDays < 0) {
            return 'due-past';
        } else if (diffDays <= 7) {
            return 'due-soon';
        } else {
            return 'due-future';
        }
    };

    const safeString = (value) => {
        return value ? String(value).toLowerCase() : '';
    };

    const filteredIssues = issues.filter((issue) => {
        // Match search term logic
        const matchesSearch = (
            safeString(issue.id).includes(searchTerm.toLowerCase()) ||
            safeString(issue.Status).includes(searchTerm.toLowerCase()) ||
            safeString(issue.Title).includes(searchTerm.toLowerCase()) ||
            safeString(issue.IssueName).includes(searchTerm.toLowerCase()) ||
            safeString(issue.Severity).includes(searchTerm.toLowerCase()) ||
            safeString(issue.ReportedByName).includes(searchTerm.toLowerCase()) ||
            safeString(issue.AssignedToName).includes(searchTerm.toLowerCase()) ||
            safeString(issue.Team).includes(searchTerm.toLowerCase()) ||
            safeString(issue.Description).includes(searchTerm.toLowerCase())
        );

        // Matching preferences (job number) remains as before:
        const matchesPreference = selectedPreferences.length === 0 || selectedPreferences.includes(issue.Title);

        // Match statuses logic
        const defaultStatuses = ['unassigned', 'active'];
        if (showResolved) {
            defaultStatuses.push('resolved');
        }
        if (showClosed) {
            defaultStatuses.push('closed');
        }
        const matchesStatus = defaultStatuses.includes(issue.Status.toLowerCase());

        // New filtering logic for Internal/External.
        // (Assumes issue.IntExt is an array of strings such as ['Internal'] or ['Internal', 'External'])
        let matchesIntExt = false;
        if (internalFilter && externalFilter) {
            // If both filters are selected, show issues that are either internal or external.
            matchesIntExt = issue.IntExt && (issue.IntExt.includes("Internal") || issue.IntExt.includes("External"));
        } else if (internalFilter) {
            matchesIntExt = issue.IntExt && issue.IntExt.includes("Internal");
        } else if (externalFilter) {
            matchesIntExt = issue.IntExt && issue.IntExt.includes("External");
        }
        // Return only issues that meet all criteria.
        return matchesSearch && matchesPreference && matchesStatus && matchesIntExt;
    });

    const handleSelectAll = () => {
        if (selectedIssues.length === filteredIssues.length) {
            // Deselect if all are selected
            setSelectedIssues([]);
        } else {
            // Otherwise, select all filtered issues
            setSelectedIssues(filteredIssues);
        }
    };

    const sortByKey = (key) => {
        let sortedIssues = [...issues];
        const direction = sortConfig.key === key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        setSortConfig({ key, direction });

        sortedIssues.sort((a, b) => {
            let aValue = a[key];
            let bValue = b[key];

            // Special handling for numeric or string comparison:
            if (key === 'id' || key === 'Title') {
                const aNum = parseInt(aValue, 10);
                const bNum = parseInt(bValue, 10);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    aValue = aNum;
                    bValue = bNum;
                } else {
                    aValue = aValue ? aValue.toString().toLowerCase() : '';
                    bValue = bValue ? bValue.toString().toLowerCase() : '';
                }
            } else if (key === 'Severity') {
                const severityOrder = { high: 1, medium: 2, low: 3 };
                aValue = severityOrder[aValue.toLowerCase()] || 0;
                bValue = severityOrder[bValue.toLowerCase()] || 0;
            } else if (key === 'Status') {
                const statusOrder = { unassigned: 1, active: 2, resolved: 3, closed: 4 };
                aValue = statusOrder[aValue.toLowerCase()] || 0;
                bValue = statusOrder[bValue.toLowerCase()] || 0;
            } else {
                aValue = aValue ? aValue.toString().toLowerCase() : '';
                bValue = bValue ? bValue.toString().toLowerCase() : '';
            }

            if (aValue < bValue) {
                return direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

        setIssues(sortedIssues);
    };

    const exportCSV = () => {
        if (selectedIssues.length === 0) {
            alert("No issues selected for export.");
            return;
        }

        // Define CSV headers
        const headers = [
            "ID", "Priority", "Job Number", "Team", "Issue", "Originator", "Date Opened",
            "Actions/Updates", "Responsible", "Due Date", "Status"
        ];

        // Convert selected issues to CSV format
        const csvRows = [headers.join(",")]; // Add headers as the first row

        selectedIssues.forEach(issue => {
            const responsibleNames = (issue.AssignedToName ?? '')
                .split(',')
                .map(name => name.trim())
                .join('; ');
            const row = [
                issue.id ?? '',
                issue.Severity ?? '',
                issue.Title ?? '',
                issue.Team ?? '',
                issue.IssueName ?? '',
                issue.ReportedByName ?? '',
                issue.DateReported ?? '',
                issue.Description ?? '',
                responsibleNames,
                issue.DueDate ?? '',
                issue.Status ?? ''
            ];
            csvRows.push(row.map(value => `"${value}"`).join(","));
        });

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "exported_issues.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const printSelectedIssues = () => {    
        const printWindow = window.open('', '_blank');
        const printContent = `
            <html>
            <head>
                <title>Print Selected Issues</title>
                <style>
                    /* Set page to landscape */
                    @page {
                        size: landscape;
                        margin: 0.1in;
                    }
                    body {
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                        margin: 0;
                        padding: 0;
                        font-size: 12px;
                        color: #333;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .header {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        position: relative;
                        margin-bottom: 20px;
                        padding: 10px 0;
                        border-bottom: 2px solid #007acc;
                    }
                    .title {
                        font-size: 20px;
                        font-weight: bold;
                        color: #007acc;
                        margin-bottom: 1rem;
                    }
                    .logo {
                        position: absolute;
                        right: 0;
                        height: 50px;
                        width: auto;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        table-layout: fixed;
                        margin: 0;
                    }
                    th, td {
                        border: 1px solid #ddd;
                        padding: 8px;
                        text-align: center;
                        word-wrap: break-word;
                    }
                    th {
                        background-color: #007acc;
                        color: white;
                        font-weight: bold;
                        font-size: 9px;
                        padding-top: 1rem;
                        padding-bottom: 1rem;
                    }
                    tr:nth-child(even):not(.status-closed) {
                        background-color: #f9f9f9;
                    }
                    td {
                        font-size: 9px;
                        color: #000000;
                    }
                    /* Set fixed widths for columns based on requirements */
                    th.id, td.id { width: 3%; }
                    th.priority, td.priority { width: 5%; }
                    th.jobNumber, td.jobNumber { width: 5%; }
                    th.team, td.team { width: 8%; }
                    th.issue, td.issue { width: 25%; }
                    th.originator, td.originator { width: 10%; }
                    th.dateOpened, td.dateOpened { width: 8%; }
                    th.actionsUpdates, td.actionsUpdates { width: 25%; }
                    th.responsible, td.responsible { width: 10%; }
                    th.dueDate, td.dueDate { width: 8%; }
                    th.status, td.status { width: 7%; }
        
                    /* Severity colors */
                    .priority-high {
                        background-color: #DA9694;
                        color: #8a0b0f;
                        font-weight: bold;
                    }
                    .priority-medium {
                        background-color: #FCD5B4;
                        color: #AD5818;
                        font-weight: bold;
                    }
                    .priority-low {
                        background-color: #C4D79B;
                        color: #415733;
                        font-weight: bold;
                    }
        
                    /* Closed status background, overriding any alternating row colors */
                    .status-closed td {
                        background-color: #bebebe !important;
                    }
        
                    /* Align Issue, Actions/Updates, and Responsible columns to left */
                    td.issue, td.actionsUpdates {
                        text-align: left;
                        white-space: pre-wrap;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="title">SPM Open Issues</div>
                    <img src="${Logo}" alt="Company Logo" class="logo" />
                </div>
                <table>
                    <thead>
                        <tr>
                            <th class="id">ID</th>
                            <th class="priority">Priority</th>
                            <th class="jobNumber">Job</th>
                            <th class="team">Team</th>
                            <th class="issue">Issue</th>
                            <th class="originator">Originator</th>
                            <th class="dateOpened">Date Opened</th>
                            <th class="actionsUpdates">Actions/Updates</th>
                            <th class="responsible">Responsible</th>
                            <th class="dueDate">Due Date</th>
                            <th class="status">Status</th>                            
                        </tr>
                    </thead>
                    <tbody>
                        ${selectedIssues.map(issue => {
                            const severityClass = issue.Status === 'Closed' ? '' : 
                                issue.Severity === 'High' ? 'priority-high' : 
                                issue.Severity === 'Medium' ? 'priority-medium' : 
                                issue.Severity === 'Low' ? 'priority-low' : '';
                            const rowClass = issue.Status === 'Closed' ? 'status-closed' : '';
                            const responsibleNames = (issue.AssignedToName ?? '')
                                .split(',')
                                .map(name => name.trim())
                                .join('<br />');
                            return `
                                <tr class="${rowClass}">
                                    <td class="id">${issue.id ?? ''}</td>
                                    <td class="priority ${severityClass}">${issue.Severity ?? ''}</td>
                                    <td class="jobNumber">${issue.Title ?? ''}</td>
                                    <td class="team">${issue.Team ?? ''}</td>
                                    <td class="issue">${issue.IssueName ?? ''}</td>
                                    <td class="originator">${issue.ReportedByName ?? ''}</td>
                                    <td class="dateOpened">${issue.DateReported ?? ''}</td>
                                    <td class="actionsUpdates">${issue.Description ?? ''}</td>
                                    <td class="responsible">${responsibleNames}</td>
                                    <td class="dueDate">${issue.DueDate ?? ''}</td>
                                    <td class="status">${issue.Status ?? ''}</td>       
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    return (
        <main className="issues-container" onClick={handleClickOutside}>
            <h1>Open Issues</h1>
            <div className="search-create-container">
                <input
                    type="text"
                    placeholder="Search issues..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="search-bar"
                />
            </div>

            <div className="second-row">
                <div className="filters-container">
                    <div className="filter-group">
                        <span className="filter-label">Type:</span>
                        <div className="checkbox-wrapper">
                        <input
                            type="checkbox"
                            id="filterInternal"
                            checked={internalFilter}
                            onChange={(e) => {
                            // Prevent both filters from being unchecked.
                            if (!e.target.checked && !externalFilter) return;
                            setInternalFilter(e.target.checked);
                            }}
                        />
                        <label htmlFor="filterInternal">Internal</label>
                        </div>
                        <div className="checkbox-wrapper">
                        <input
                            type="checkbox"
                            id="filterExternal"
                            checked={externalFilter}
                            onChange={(e) => {
                            if (!e.target.checked && !internalFilter) return;
                            setExternalFilter(e.target.checked);
                            }}
                        />
                        <label htmlFor="filterExternal">External</label>
                        </div>
                    </div>
                    <div className="filter-group">
                        <span className="filter-label">Status:</span>
                        <div className="checkbox-wrapper">
                        <input
                            type="checkbox"
                            id="showResolved"
                            checked={showResolved}
                            onChange={() => setShowResolved(!showResolved)}
                        />
                        <label htmlFor="showResolved">Resolved</label>
                        </div>
                        <div className="checkbox-wrapper">
                        <input
                            type="checkbox"
                            id="showClosed"
                            checked={showClosed}
                            onChange={() => setShowClosed(!showClosed)}
                        />
                        <label htmlFor="showClosed">Closed</label>
                        </div>
                    </div>
                </div>

                    
                
                <div className="preferences">
                    <div className="all-preferences">
                        {Object.values(preferences).filter(Boolean).map((id) => 
                            id ? (
                                <button
                                    key={id}
                                    className={`pref-button ${selectedPreferences.includes(id) ? 'active' : ''}`}
                                    onClick={() => handlePreferenceClick(id)}
                                >
                                    {id}
                                </button>
                            ) : null
                        )}
                    </div>
                    <button
                        onClick={() => setShowPreferencesModal(true)}
                        className={`pref-button-add`}
                    >
                        +
                    </button>
                </div>
                <div className="button-container">
                    <button onClick={() => setShowCreateModal(true)} className="create-button">
                        <i className="fas fa-plus"/> Create
                    </button>
                    <button onClick={exportCSV} className="create-button" disabled={selectedIssues.length === 0}>
                        <i className="fas fa-file-csv"/> CSV
                    </button>
                    <button onClick={printSelectedIssues} className="create-button" disabled={selectedIssues.length === 0}>
                        <i className="fas fa-print"/> Print
                    </button>
                </div>
            </div>

            <div className="table-wrapper">
                <table className="issues-table">
                    <thead>
                        <tr>
                            <th onClick={handleSelectAll}>
                                {selectedIssues.length !== filteredIssues.length || filteredIssues.length === 0 ? (
                                    <i className="fas fa-square" />
                                ) : (
                                    <i className="fas fa-check-square" />
                                )}
                            </th>
                            <th onClick={() => sortByKey('id')}>ID <i className="fas fa-sort" /></th>
                            <th onClick={() => sortByKey('Title')}>Job # <i className="fas fa-sort" /></th>
                            <th>Issue Name</th>
                            <th onClick={() => sortByKey('AssignedToName')}>Assigned To <i className="fas fa-sort" /></th>
                            <th onClick={() => sortByKey('DateReported')}>Days <i className="fas fa-sort" /></th>
                            <th onClick={() => sortByKey('Severity')}>Severity <i className="fas fa-sort" /></th>
                            <th onClick={() => sortByKey('Status')}>Status <i className="fas fa-sort" /></th>
                            <th onClick={() => sortByKey('DueDate')}>Due Date <i className="fas fa-sort" /></th>
                            <th className="hide-mobile">Description</th>
                            <th>Files</th>
                            <th className="hide-mobile">LL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredIssues.length > 0 ? (
                            filteredIssues.map((issue, index) => (
                                <tr
                                    key={index}
                                    onClick={(e) => {
                                        if (!e.target.closest('.checkbox-cell')) {
                                            if (!e.target.type) handleRowClick(issue);
                                        }
                                    }}
                                    onContextMenu={(e) => handleRowRightClick(issue, e, index)}
                                    className={`issue-row ${selectedRowIndex === index ? 'row-selected' : ''}`}
                                >
                                    <td className="checkbox-cell">
                                        <input
                                            type="checkbox"
                                            checked={selectedIssues.includes(issue)}
                                            onChange={(e) => handleIssueSelection(issue, e)}
                                        />
                                    </td>
                                    <td>{issue.id || ''}</td>
                                    <td><strong>{issue.Title || ''}</strong></td>
                                    <td>{issue.IssueName || ''}</td>
                                    <td>
                                        {typeof issue.AssignedToName === 'string'
                                            ? issue.AssignedToName.split(',').map((name, index) => (
                                                <React.Fragment key={index}>
                                                    {name.trim()}
                                                    <br />
                                                </React.Fragment>
                                            ))
                                            : issue.AssignedToName || ''}
                                    </td>
                                    <td>{issue.DateReported ? Math.abs(Math.floor((today - new Date(issue.DateReported)) / (1000 * 60 * 60 * 24))) : ''}</td>
                                    <td>
                                        <span className={`issue.Severity ? severity-${issue.Severity.toLowerCase()} : ''`}>
                                            {issue.Severity || ''}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`issue.Status ? status-${issue.Status.toLowerCase()} : ''`}>
                                            {issue.Status || ''}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={getDueDateClass(issue.DueDate)}>
                                            {issue.DueDate || ''}
                                        </span>
                                    </td>
                                    <td className="description hide-mobile">{issue.Description || ''}</td>
                                    <td className="files">
                                    {issue.AttachmentURL ? (
                                        <i className="fas fa-check"></i>
                                    ) : (
                                        ''
                                    )}
                                    </td>
                                    <td className="files hide-mobile">
                                    {issue.LessonsLearned ? (
                                        <i className="fas fa-check"></i>
                                    ) : (
                                        ''
                                    )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="11">No issues found</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {showContextMenu && (
                    <ContextMenu
                        x={contextMenuPosition.x}
                        y={contextMenuPosition.y}
                        onUpdate={handleUpdateClick}
                        onDelete={handleDeleteClick}
                    />
                )}

                {showCreateModal && (
                    <div className="modal">
                        <div className="modal-content">
                            <span className="close" onClick={() => { setShowCreateModal(false); loadData(); }}>&times;</span>
                            <Create />
                        </div>
                    </div>
                )}

                {showUpdateModal && selectedIssue && (
                    <div className="modal">
                        <div className="modal-content">
                            <span className="close" onClick={() => setShowUpdateModal(false)}>&times;</span>
                            <Update issue={selectedIssue} closeModal={() => { setShowUpdateModal(false); loadData(); }} />
                        </div>
                    </div>
                )}

                {showDeleteModal && (
                    <DeleteConfirmationModal
                        show={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={confirmDelete}
                    />
                )}
                {showPreferencesModal && (
                    <div className="modal">
                        <div className="preferences-content">
                            <span className="close" onClick={() => setShowPreferencesModal(false)}>&times;</span>
                            <PreferencesModal preferences={preferences} closeModal={() => { setShowPreferencesModal(false); loadPreferences(); }} />
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
};

export default Issues;
